import {A, E} from './util.mjs'
import * as sr from './shims/react.mjs'
import * as a from '@mitranim/js/all.mjs'
import * as dg from '@mitranim/js/dom_global'
import * as dr from '@mitranim/js/dom_reg.mjs'
import * as c from './conf.mjs'
import * as d from './dat.mjs'
import * as u from './util.mjs'
import * as n from './net.mjs'
import * as em from './elem-misc.mjs'
import * as s from './views/svg.mjs'

/*
Intended for subclassing, only for submit buttons.
For normal buttons, see `Btn`.
*/
export class BtnSubmit extends u.MixElem(dg.global.HTMLButtonElement) {
  static {dr.reg(this)}

  connectedCallback() {
    // SSR may disable interactive elements; see `impl.md`.
    u.enableAfterSsr(this)
  }

  get A() {return A.ssrDisabled()}
  init() {return E(this, this.A)}

  /*
  Custom support for "readonly", which is missing from the native APIs. Unlike
  the "disabled" state, "readonly" doesn't lose focus. Our implementation
  of "readonly" is ONLY for "type=button". It does NOT prevent form submission
  for "type=submit".
  */
  get readOnly() {return this.hasAttribute(`readonly`)}

  set readOnly(val) {
    if (val) this.setAttribute(`readonly`, ``)
    else this.removeAttribute(`readonly`)
  }

  // Invoked by `Form` during form submission.
  onFormDisabled(val) {this.disabled = a.laxBool(val)}
}

/*
Usage: subclass, override `.perform`.
By default, errors are reported via global toast.
*/
export class Btn extends em.MixExecable(BtnSubmit) {
  static customName = `a-btn`
  static {dr.reg(this)}

  get A() {return super.A.button()}

  connectedCallback() {
    if (!c.PROD) this.validateType()
    super.connectedCallback()
    if (this.type === `button`) this.onclick = this.onClick
  }

  // Seems redundant. May be useful for overrides.
  async onClick(event) {await this.exec(event)}

  // Tentative. Might revise or move to a subclass.
  perform(event) {
    const type = this.dataset.event
    if (type && event.type !== type) {
      u.dispatch(this, type, this.getProps())
    }
  }

  // Called by `MixExecable`.
  setDisabled(val) {this.readOnly = a.laxBool(val)}

  validateType() {
    if (this.type === `button`) return
    if (this.hasAttribute(`type`)) return
    throw Error(`invalid implicit [type=submit] on ${a.show(this)}`)
  }
}

export class BtnScroll extends Btn {
  static {dr.reg(this)}
  get id() {return this.dataset.id}
  perform() {u.scrollTo(this.id)}
}

export class BtnScrollTop extends Btn {
  static {dr.reg(this)}
  perform() {window.scrollTo(0, 0)}
}

export class BtnAuthLogin extends Btn {
  static customName = a.reqValidStr(c.IS_BTN_AUTH_LOGIN.is)
  static {dr.reg(this)}

  async perform() {
    const mod = await u.imp(import(`./views/auth-magic.mjs`))
    mod.openAuthMagic()
  }
}

export class BtnAuthLogout extends Btn {
  static {dr.reg(this)}
  async perform() {u.logoutAndReload(this.ctx)}
}

export class BtnAuthReg extends Btn {
  static customName = a.reqValidStr(c.IS_BTN_AUTH_REG.is)
  static {dr.reg(this)}

  async perform() {
    const mod = await u.imp(import(`./views/auth-reg.mjs`))
    mod.openRegister(this.ctx)
  }
}

class BtnLoadMore extends u.MixElemVisObs(Btn) {
  static props() {return {is: this.customName, ...u.testProps(`LoadMoreOpt`)}}

  fetch() {throw u.errImplementInSubclass()}
  comp() {throw u.errImplementInSubclass()}

  autoLoad() {return c.AUTO_LOAD_MORE && `autoLoad` in this.dataset}
  onVisible() {if (this.autoLoad()) this.exec()}
  getParams() {return a.jsonDecode(this.dataset.params)}
  setParams(val) {return this.dataset.params = a.jsonEncode(val), this}

  // TODO change the button's appearance or text to indicate that we're loading.
  // Simply disabling the button is not completely informative.
  async perform() {
    const params = a.optDict(this.getParams())
    if (!params) return

    const props = this.getProps()
    const target = u.sel(document, this.dataset.sel)
    if (!target) return

    const [page, comp] = await u.plist()
      // Must return a `ClsPage` instance.
      .add(this.fetch(params))
      // Must return a page-rendering function.
      .add(this.comp())

    if (this.ctx?.signal.aborted) return

    // Might be unnecessary, TODO recheck.
    page.setParams(params)

    u.ren.appendChi(target, await comp(this.ctx, props, page.getEnts()))

    this.setParams(page.nextPageParams())
    if (!page.hasNext()) this.remove()
  }
}

export class BtnLoadMoreArticleFeed extends BtnLoadMore {
  static {dr.reg(this)}
  fetch(params) {return this.ctx.dat.articlePage(params).valueTask()}
  async comp() {return (await u.imp(import(`./views/cards.mjs`))).ArticleFeedItems}
}

export class BtnLoadMoreMyArticleFeed extends BtnLoadMoreArticleFeed {
  static {dr.reg(this)}
  async comp() {return (await u.imp(import(`./views/my-acc-articles.mjs`))).MyArticleFeedItems}
}

export class BtnLoadMoreServiceFeed extends BtnLoadMore {
  static {dr.reg(this)}
  fetch(params) {return this.ctx.dat.servicePage(params).valueTask()}
  async comp() {return (await u.imp(import(`./views/cards.mjs`))).ServiceFeedItems}
}

export class BtnLoadMoreVideoFeed extends BtnLoadMore {
  static {dr.reg(this)}
  fetch(params) {return this.ctx.dat.servicePage(params).valueTask()}
  async comp() {return (await u.imp(import(`./views/cards.mjs`))).VideoFeedItems}
}

export class BtnLoadMoreBookings extends BtnLoadMore {
  static {dr.reg(this)}
  fetch(params) {return this.ctx.dat.bookingPage(params).valueTask()}
  async comp() {return (await u.imp(import(`./views/my-purchases-sales.mjs`))).BookingFeedItems}
}

export class BtnLoadMorePersonFeed extends BtnLoadMore {
  static {dr.reg(this)}
  fetch(params) {return this.ctx.dat.practPage(params).valueTask()}
  async comp() {return (await u.imp(import(`./views/cards.mjs`))).PersonFeedItems}
}

export class BtnLoadMoreOrgFeed extends BtnLoadMore {
  static {dr.reg(this)}
  fetch(params) {return this.ctx.dat.orgPage(params).valueTask()}
  async comp() {return (await u.imp(import(`./views/cards.mjs`))).OrgFeedItems}
}

export class BtnLoadMoreLuminaryFeed extends BtnLoadMore {
  static {dr.reg(this)}
  fetch(params) {return this.ctx.dat.luminaryPage(params).valueTask()}
  async comp() {return (await u.imp(import(`./views/cards.mjs`))).LuminaryFeedItems}
}

/*
This uses React because the dialog body tends to be `md.TaskDialogModalBody`.
TODO convert dialogs to pure custom elements.
*/
export class BtnTaskDialog extends Btn {
  async perform() {
    const [md, DialogComp] = await u.plist()
      .add(u.imp(import(`./views/modals.mjs`)))
      .add(this.getDialog())

    const modal = md.Modal()
      .reactChi(sr.E(md.ModalTable, {}, sr.E(DialogComp, this.getProps())))
      .show()

    const val = await modal.task
    if (a.isSome(val)) await this.reinit(val)
    return val
  }

  // Must override in subclass.
  getDialog() {throw u.errImplementInSubclass()}

  // May override in subclass.
  reinit(val) {return this.setProps(val)}
}

export class BtnCardControlDialog extends BtnTaskDialog {
  get A() {return A.button().cls(c.CLS_BTN_ICON_STD)}
  reinit(val) {return this.init(val)}
}

export class BtnCardControlShowHide extends BtnCardControlDialog {
  async init(ent) {
    const id = u.pk(ent)
    const isHidden = a.reqBool(ent.isHidden)
    const xln = await u.imp(import(`./localization/xln.mjs`))

    this.setProps({id, isHidden})

    return E(
      this,
      this.A.tooltip(isHidden ? xln.XLN_SHOW(this.ctx) : xln.XLN_HIDE(this.ctx)),
      isHidden ? s.Eye() : s.EyeSlash(),
    )
  }
}

export class ModalToggle extends Btn {
  static customName = `modal-toggle`
  static {dr.reg(this)}

  perform() {
    const {sel} = this.dataset
    if (!sel) return
    for (const node of u.selAll(document, sel)) node.toggle()
  }
}

export class ModalClose extends Btn {
  static {dr.reg(this)}

  perform(event) {
    super.perform(event)
    u.dispatch(this, c.EVENT_MODAL_HIDE)
  }
}

export class BtnOpenMobileApp extends Btn {
  static customName = `open-mobile-app`
  static {dr.reg(this)}

  async perform() {
    return (await u.imp(import(`./util-misc.mjs`))).redirectMobile(this.ctx)
  }
}

export class BtnPublish extends Btn {
  static {dr.reg(this)}

  init({endpoint, provider, text, ...props}) {
    a.reqInst(endpoint, d.PublishEndpoint)
    a.reqInst(provider, d.SocialProvider)

    return E(
      this,
      super.A
        .with(props)
        .isFrom(this)
        .dataProps({endpoint, provider})
        .cls(`btn-accent --tiny ws-nowrap`)
        // @TK: TODO: make this unnecessary.
        .style(`height: auto`),
      text || `Publish`,
    )
  }

  endpoint() {return new d.PublishEndpoint(this.getProps().endpoint)}
  provider() {return new d.SocialProvider(this.getProps().provider)}

  async perform() {
    // TODO generalize, shorten.
    try {
      await a.resOk(this.ctx.fetch(this.endpoint().reqOpt()))
    }
    catch (err) {
      if (u.isErrAuthExternal(err)) {
        await this.onErrAuthExternal(err)
        return
      }
      throw err
    }

    this.textContent = `Published!`

    // Keep disabled forever.
    this.incCounter()
  }

  async onErrAuthExternal(err) {
    const mod = await u.imp(import(`./views/my-acc-social.mjs`))
    em.Notif.show(mod.SocialProviderErrorToast({ctx: this.ctx, err, prov: this.provider()}))
  }
}

export class BtnFollow extends Btn {
  static {dr.reg(this)}

  pk() {return this.dataset.chanId}
  isFollowed() {return a.boolOpt(this.dataset.isFollowed)}
  setIsFollowed(val) {return this.dataset.isFollowed = a.reqBool(val), this}

  preinit(chanId) {
    this.dataset.chanId = a.reqPk(chanId)
    this.mutInp(this.pageDat().inp)
  }

  init() {return this.initFrom(this.buk())}

  initFrom(buk) {
    const isFollowed = a.includes(
      buk?.get.units.followedChanIds,
      a.pk(this),
    )
    this.setIsFollowed(isFollowed)

    return E(
      this,
      A
        .button()
        .cls(c.CLS_BTN_ICON_STD)
        .test(`BtnFollow`)
        .tooltip(isFollowed ? `Unfollow` : `Follow`),
      isFollowed ? s.HeartFilled() : s.HeartEmpty(),
    )
  }

  async perform() {
    const inp = new d.BukInp
    const subscribe = !this.isFollowed()

    this.mutInp(inp)
    if (subscribe) inp.act.chanMetas.subscribe(a.pk(this))
    else inp.act.chanMetas.unsubscribe(a.pk(this))

    this.initFrom(await this.pageDat().bukFetch(inp))
  }

  mutInp(inp) {inp.get.units.followedChanIds = true}
}

/*
Incomplete. Lacks support for indicating success.
Compare the full-featured `CopyToClipboard`.
*/
export class BtnClip extends Btn {
  static {dr.reg(this)}
  perform() {u.copyToClipboard(this.dataset.text)}
}

export class BtnReport extends Btn {
  static {dr.reg(this)}

  async perform() {
    const [md, rep] = await u.plist()
      .add(import(`./views/modals.mjs`))
      .add(import(`./views/report.mjs`))

    function close() {modal.hide()}

    const modal = md.Modal().reactChi(
      sr.E(md.ModalTable, {},
        sr.E(md.NewModalBody, {ctx: this.ctx, class: `maw-col-5`},
          sr.E(rep.ReportForm, {onSuccess: close}),
        ),
      ),
    ).show()
  }
}

export class BtnPay extends Btn {
  static {dr.reg(this)}

  get A() {
    return super.A.cls(c.CLS_BUY_BTN)
  }

  init(cents, props, ...chi) {
    this.setProps(a.reqFin(cents))
    return E(this, this.A.with(props), ...chi)
  }

  async perform() {
    const {ctx} = this
    const fc = await u.imp(import(`./views/fast-checkout.mjs`))

    const subj = await n.createPaymentSubjectForDeposit(ctx, {
      currency: c.CURRENCY_DEFAULT.code,
      cents: u.moneyUnitsToCents(this.getProps()),
    })

    if (!await fc.openFastCheckoutModal({ctx, paymentSubject: subj})) return
    await ctx.pageDat.refetchBalance()
  }
}
